import React from 'react';
import { Box } from '@mui/material';
import { useFormContext, useWatch, FieldValues, Path, Controller, PathValue } from 'react-hook-form';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import PathControlledDropdown, {
  ControlledDropdown
} from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/components/ui/PathControlledDropdown';
import {
  ForTheFirstTimeOptionsSchema,
  PredefinedComparisonOptionsSchema,
  PredefinedPeriodRangesSchema,
  TimeRangeModesSchema,
  WeekReferenceOperatorSchema
} from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/schema/AlertBuilderSchema';
import {
  TimeRangeModesType,
  TimeConfigurationType,
  CustomRangeType
} from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/schema/AlertBuilderSchemaTypes';
import useCurrentAndFutureWeekId from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/hooks/useCurrentAndFutureWeekId';
import {
  createTimeConfiguration,
  DEFAULT_RELATIVE_BEFORE_WEEK_TIME_CONFIGURATION,
  DEFAULT_RELATIVE_AFTER_WEEK_TIME_CONFIGURATION,
  DEFAULT_RELATIVE_REFERENCE,
  DEFAULT_RELATIVE_OPERATOR,
  DEFAULT_RELATIVE_OFFSET,
  getLargestWeekId,
  UI_WEEK_REFERENCES,
  DROPDOWN_WIDTH
} from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/utils';
import colors from 'src/utils/colors';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import PathControlledNumberInput from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/components/ui/PathControlledNumberInput';
import PathControlledCheckbox from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/components/ui/PathControlledCheckbox';

type Props<T extends FieldValues> = {
  groupIndex: number;
  blockIndex: number;
};

function RuleBlockTimeRange<T extends FieldValues>({ groupIndex, blockIndex }: Props<T>) {
  const { setValue, control } = useFormContext<T>();
  const allWeekIds = useCurrentAndFutureWeekId();

  // Base paths for this rule block
  const basePath = `queryJSON.rules.ruleGroups.${groupIndex}.ruleBlocks.${blockIndex}` as Path<T>;
  const timeConfigPath = `${basePath}.timeConfiguration` as Path<T>;

  // Watch the time configuration to determine current mode and values
  const timeConfig = useWatch({ name: timeConfigPath }) as TimeConfigurationType;
  const weekIdOptions = allWeekIds;

  // Helper function to update time configuration with consistent options
  const updateTimeConfiguration = React.useCallback(
    (newConfig: TimeConfigurationType) => {
      setTimeout(() => {
        setValue(timeConfigPath, newConfig as PathValue<T, typeof timeConfigPath>, {
          shouldValidate: false,
          shouldDirty: true,
          shouldTouch: false
        });
      }, 0);
    },
    [setValue, timeConfigPath]
  );

  // Keep track of the previous mode to detect changes
  const prevModeRef = React.useRef<TimeRangeModesType | undefined>(timeConfig?.mode);

  // Combined effect: handle mode reset, and Before/After Week relative toggles
  React.useEffect(() => {
    const currentMode = timeConfig?.mode;

    // Handle mode change reset first
    if (prevModeRef.current && prevModeRef.current !== currentMode && currentMode) {
      const newTimeConfig = createTimeConfiguration(currentMode as TimeRangeModesType, allWeekIds);
      updateTimeConfiguration(newTimeConfig);
      prevModeRef.current = currentMode;
      return; // Avoid running other logic in the same tick after a reset
    }

    // Always update prev mode ref
    prevModeRef.current = currentMode;

    // Handle Before Week -> Relative toggle
    if (timeConfig?.mode === 'Before Week') {
      const currentRelativeBeforeWeek = timeConfig.beforeWeek?.useRelativeBeforeWeek || false;
      if (currentRelativeBeforeWeek === true && timeConfig.beforeWeek && 'beforeWeekValue' in timeConfig.beforeWeek) {
        updateTimeConfiguration(DEFAULT_RELATIVE_BEFORE_WEEK_TIME_CONFIGURATION);
        return;
      }
    }

    // Handle After Week -> Relative toggle
    if (timeConfig?.mode === 'After Week') {
      const currentRelativeAfterWeek = timeConfig.afterWeek?.useRelativeAfterWeek || false;
      if (currentRelativeAfterWeek === true && timeConfig.afterWeek && 'afterWeekValue' in timeConfig.afterWeek) {
        updateTimeConfiguration(DEFAULT_RELATIVE_AFTER_WEEK_TIME_CONFIGURATION);
        return;
      }
    }
  }, [timeConfig, allWeekIds, updateTimeConfiguration]);

  // Check if Before Week or After Week is in relative mode to adjust layout
  const isBeforeWeekRelative = timeConfig.mode === 'Before Week' && timeConfig.beforeWeek?.useRelativeBeforeWeek;
  const isAfterWeekRelative = timeConfig.mode === 'After Week' && timeConfig.afterWeek?.useRelativeAfterWeek;
  const isCustomStartRelative = timeConfig.mode === 'Custom Range' && timeConfig.customRange?.useRelativeStartWeek;
  const isCustomEndRelative = timeConfig.mode === 'Custom Range' && timeConfig.customRange?.useRelativeEndWeek;

  // Sync Custom Range relative/absolute fields for both Start and End in one pass
  React.useEffect(() => {
    if (timeConfig?.mode !== 'Custom Range') return;
    const customRange: CustomRangeType = timeConfig.customRange || {};
    const newCustomRange: CustomRangeType = { ...customRange };
    let changed = false;

    // Start side: enable relative from absolute
    if (newCustomRange.useRelativeStartWeek === true && 'startWeek' in newCustomRange) {
      delete newCustomRange.startWeek;
      if (newCustomRange.relativeStartReference === undefined)
        newCustomRange.relativeStartReference = DEFAULT_RELATIVE_REFERENCE;
      if (newCustomRange.relativeStartOperator === undefined)
        newCustomRange.relativeStartOperator = DEFAULT_RELATIVE_OPERATOR;
      if (newCustomRange.relativeStartOffset === undefined)
        newCustomRange.relativeStartOffset = DEFAULT_RELATIVE_OFFSET;
      changed = true;
    }

    // Start side: disable relative -> ensure absolute, clear relative fields
    const hasStartRelativeFields =
      'relativeStartReference' in newCustomRange ||
      'relativeStartOperator' in newCustomRange ||
      'relativeStartOffset' in newCustomRange;
    if (newCustomRange.useRelativeStartWeek === false && hasStartRelativeFields) {
      delete newCustomRange.relativeStartReference;
      delete newCustomRange.relativeStartOperator;
      delete newCustomRange.relativeStartOffset;
      if (newCustomRange.startWeek === undefined) newCustomRange.startWeek = getLargestWeekId(weekIdOptions);
      changed = true;
    }

    // End side: enable relative from absolute
    if (newCustomRange.useRelativeEndWeek === true && 'endWeek' in newCustomRange) {
      delete newCustomRange.endWeek;
      if (newCustomRange.relativeEndReference === undefined)
        newCustomRange.relativeEndReference = DEFAULT_RELATIVE_REFERENCE;
      if (newCustomRange.relativeEndOperator === undefined)
        newCustomRange.relativeEndOperator = DEFAULT_RELATIVE_OPERATOR;
      if (newCustomRange.relativeEndOffset === undefined) newCustomRange.relativeEndOffset = DEFAULT_RELATIVE_OFFSET;
      changed = true;
    }

    // End side: disable relative -> ensure absolute, clear relative fields
    const hasEndRelativeFields =
      'relativeEndReference' in newCustomRange ||
      'relativeEndOperator' in newCustomRange ||
      'relativeEndOffset' in newCustomRange;
    if (newCustomRange.useRelativeEndWeek === false && hasEndRelativeFields) {
      delete newCustomRange.relativeEndReference;
      delete newCustomRange.relativeEndOperator;
      delete newCustomRange.relativeEndOffset;
      if (newCustomRange.endWeek === undefined) newCustomRange.endWeek = getLargestWeekId(weekIdOptions);
      changed = true;
    }

    if (changed) {
      const newTimeConfig: TimeConfigurationType = {
        ...timeConfig,
        customRange: newCustomRange
      } as TimeConfigurationType;

      updateTimeConfiguration(newTimeConfig);
    }
  }, [timeConfig, weekIdOptions, updateTimeConfiguration]);

  // Sync Before Week relative/absolute fields and cleanup
  React.useEffect(() => {
    if (timeConfig?.mode !== 'Before Week') return;
    const beforeWeek = timeConfig.beforeWeek || {};
    const newBeforeWeek = { ...beforeWeek };
    let changed = false;

    // Enable relative from absolute
    if (newBeforeWeek.useRelativeBeforeWeek === true && 'beforeWeekValue' in newBeforeWeek) {
      delete (newBeforeWeek as any).beforeWeekValue;
      if (newBeforeWeek.relativeBeforeWeekReference === undefined)
        newBeforeWeek.relativeBeforeWeekReference = DEFAULT_RELATIVE_REFERENCE;
      if (newBeforeWeek.relativeBeforeWeekOperator === undefined)
        newBeforeWeek.relativeBeforeWeekOperator = DEFAULT_RELATIVE_OPERATOR;
      if (newBeforeWeek.relativeBeforeWeekOffset === undefined)
        newBeforeWeek.relativeBeforeWeekOffset = DEFAULT_RELATIVE_OFFSET;
      changed = true;
    }

    // Disable relative -> ensure absolute, clear relative fields
    const hasRelativeFields =
      'relativeBeforeWeekReference' in newBeforeWeek ||
      'relativeBeforeWeekOperator' in newBeforeWeek ||
      'relativeBeforeWeekOffset' in newBeforeWeek;
    if (newBeforeWeek.useRelativeBeforeWeek === false && hasRelativeFields) {
      delete (newBeforeWeek as any).relativeBeforeWeekReference;
      delete (newBeforeWeek as any).relativeBeforeWeekOperator;
      delete (newBeforeWeek as any).relativeBeforeWeekOffset;
      if ((newBeforeWeek as any).beforeWeekValue === undefined)
        (newBeforeWeek as any).beforeWeekValue = getLargestWeekId(weekIdOptions);
      changed = true;
    }

    if (changed) {
      const newTimeConfig: TimeConfigurationType = {
        ...timeConfig,
        beforeWeek: newBeforeWeek
      } as TimeConfigurationType;

      updateTimeConfiguration(newTimeConfig);
    }
  }, [timeConfig, weekIdOptions, updateTimeConfiguration]);

  // Sync After Week relative/absolute fields and cleanup
  React.useEffect(() => {
    if (timeConfig?.mode !== 'After Week') return;
    const afterWeek = timeConfig.afterWeek || {};
    const newAfterWeek = { ...afterWeek };
    let changed = false;

    // Enable relative from absolute
    if (newAfterWeek.useRelativeAfterWeek === true && 'afterWeekValue' in newAfterWeek) {
      delete (newAfterWeek as any).afterWeekValue;
      if (newAfterWeek.relativeAfterWeekReference === undefined)
        newAfterWeek.relativeAfterWeekReference = DEFAULT_RELATIVE_REFERENCE;
      if (newAfterWeek.relativeAfterWeekOperator === undefined)
        newAfterWeek.relativeAfterWeekOperator = DEFAULT_RELATIVE_OPERATOR;
      if (newAfterWeek.relativeAfterWeekOffset === undefined)
        newAfterWeek.relativeAfterWeekOffset = DEFAULT_RELATIVE_OFFSET;
      changed = true;
    }

    // Disable relative -> ensure absolute, clear relative fields
    const hasRelativeFields =
      'relativeAfterWeekReference' in newAfterWeek ||
      'relativeAfterWeekOperator' in newAfterWeek ||
      'relativeAfterWeekOffset' in newAfterWeek;
    if (newAfterWeek.useRelativeAfterWeek === false && hasRelativeFields) {
      delete (newAfterWeek as any).relativeAfterWeekReference;
      delete (newAfterWeek as any).relativeAfterWeekOperator;
      delete (newAfterWeek as any).relativeAfterWeekOffset;
      if ((newAfterWeek as any).afterWeekValue === undefined)
        (newAfterWeek as any).afterWeekValue = getLargestWeekId(weekIdOptions);
      changed = true;
    }

    if (changed) {
      const newTimeConfig: TimeConfigurationType = {
        ...timeConfig,
        afterWeek: newAfterWeek
      } as TimeConfigurationType;

      updateTimeConfiguration(newTimeConfig);
    }
  }, [timeConfig, weekIdOptions, updateTimeConfiguration]);

  // Clean up comparisonOption when switching from Predefined Comparison to Predefined Period
  React.useEffect(() => {
    if (timeConfig?.mode === 'Predefined Period' && 'comparisonOption' in timeConfig) {
      const newTimeConfig = { ...timeConfig };
      delete (newTimeConfig as any).comparisonOption;

      updateTimeConfiguration(newTimeConfig as TimeConfigurationType);
    }
  }, [timeConfig, updateTimeConfiguration]);

  // Reusable positioned "Relative" checkbox component
  const RelativeModeCheckbox: React.FC<{ name: Path<T> }> = ({ name }) => (
    <Box
      sx={{
        position: 'absolute',
        left: '-2px',
        top: '24px',
        zIndex: 1
      }}
    >
      <PathControlledCheckbox
        name={name}
        label="Relative"
        sx={{
          '&.Mui-checked': {
            color: colors.darkBlue
          },
          '&.Mui-checked:hover': {
            backgroundColor: `${colors.darkBlue}14`
          }
        }}
      />
    </Box>
  );

  // Helper function to render a Custom Range week row inline
  const renderCustomRangeWeekRow = (
    label: string,
    isRelative: boolean,
    referenceName: Path<T>,
    operatorName: Path<T>,
    offsetName: Path<T>,
    absoluteWeekName: Path<T>,
    relativeToggleName: Path<T>
  ) => (
    <Box key={label} sx={{ flex: 1, width: '100%' }}>
      <Box sx={{ marginBottom: '4px' }}>
        <Text variant="body2" sx={{ fontWeight: 500, marginBottom: '8px', display: 'block' }}>
          {label}
        </Text>
      </Box>
      <Flex gap={16} alignItems="center" style={{ minHeight: 40 }}>
        {isRelative ? (
          <Flex gap={8} alignItems="center" style={{ minHeight: 40 }}>
            <Box sx={{ width: '159px', display: 'inline-block' }}>
              <PathControlledDropdown
                name={referenceName}
                label=""
                options={UI_WEEK_REFERENCES.map((ref) => ref.id)}
                placeholder="Select Reference Week"
              />
            </Box>
            <Box sx={{ width: '60px', display: 'inline-block' }}>
              <PathControlledDropdown name={operatorName} label="" options={WeekReferenceOperatorSchema.options} />
            </Box>
            <Box sx={{ width: '80px', display: 'inline-block' }}>
              <PathControlledNumberInput name={offsetName} label="" min={0} max={52} />
            </Box>
          </Flex>
        ) : (
          <Controller
            name={absoluteWeekName}
            control={control}
            render={({ field: { value, onChange } }) => (
              <Box sx={{ width: DROPDOWN_WIDTH, display: 'inline-block' }}>
                <ControlledDropdown
                  label=""
                  value={typeof value === 'number' ? String(value) : value || ''}
                  onChange={(newValue) => onChange(parseInt(newValue, 10))}
                  options={weekIdOptions}
                  placeholder={`Select ${label}`}
                />
              </Box>
            )}
          />
        )}

        <Box sx={{ width: '120px', display: 'flex', alignItems: 'center' }}>
          <PathControlledCheckbox
            name={relativeToggleName}
            label="Relative"
            sx={{
              '&.Mui-checked': { color: colors.darkBlue },
              '&.Mui-checked:hover': { backgroundColor: `${colors.darkBlue}14` }
            }}
          />
        </Box>
      </Flex>
    </Box>
  );

  // Render functions for different modes
  const renderBeforeWeekMode = () => (
    <>
      {/* First column - Main dropdown (reference or absolute week) */}
      <Box sx={{ flex: 1, maxWidth: DROPDOWN_WIDTH }}>
        {isBeforeWeekRelative ? (
          // Relative mode: Reference Week dropdown
          <PathControlledDropdown
            name={`${timeConfigPath}.beforeWeek.relativeBeforeWeekReference` as Path<T>}
            label=""
            options={UI_WEEK_REFERENCES.map((ref) => ref.id)}
            placeholder="Select Reference Week"
          />
        ) : (
          // Absolute mode: Week ID dropdown - convert string to number
          <Controller
            name={`${timeConfigPath}.beforeWeek.beforeWeekValue` as Path<T>}
            control={control}
            render={({ field: { value, onChange } }) => (
              <ControlledDropdown
                label=""
                value={typeof value === 'number' ? String(value) : value || ''}
                onChange={(newValue) => onChange(parseInt(newValue, 10))}
                options={weekIdOptions}
                placeholder="Select Week"
              />
            )}
          />
        )}
      </Box>

      {/* Second column - Operator dropdown for relative mode */}
      <Box sx={{ flex: 1, maxWidth: '60px' }}>
        {isBeforeWeekRelative && (
          <PathControlledDropdown
            name={`${timeConfigPath}.beforeWeek.relativeBeforeWeekOperator` as Path<T>}
            label=""
            options={WeekReferenceOperatorSchema.options}
          />
        )}
      </Box>

      {/* Third column - Offset number input for relative mode */}
      <Box sx={{ flex: 1 }}>
        {isBeforeWeekRelative && (
          <PathControlledNumberInput
            name={`${timeConfigPath}.beforeWeek.relativeBeforeWeekOffset` as Path<T>}
            label=""
            min={0}
            max={52}
          />
        )}
      </Box>
    </>
  );

  const renderAfterWeekMode = () => (
    <>
      {/* First column - Main dropdown (reference or absolute week) */}
      <Box sx={{ flex: 1, maxWidth: DROPDOWN_WIDTH }}>
        {isAfterWeekRelative ? (
          // Relative mode: Reference Week dropdown
          <PathControlledDropdown
            name={`${timeConfigPath}.afterWeek.relativeAfterWeekReference` as Path<T>}
            label=""
            options={UI_WEEK_REFERENCES.map((ref) => ref.id)}
            placeholder="Select Reference Week"
          />
        ) : (
          // Absolute mode: Week ID dropdown - convert string to number
          <Controller
            name={`${timeConfigPath}.afterWeek.afterWeekValue` as Path<T>}
            control={control}
            render={({ field: { value, onChange } }) => (
              <ControlledDropdown
                label=""
                value={typeof value === 'number' ? String(value) : value || ''}
                onChange={(newValue) => onChange(parseInt(newValue, 10))}
                options={weekIdOptions}
                placeholder="Select Week"
              />
            )}
          />
        )}
      </Box>

      {/* Second column - Operator dropdown for relative mode */}
      <Box sx={{ flex: 1, maxWidth: '60px' }}>
        {isAfterWeekRelative ? (
          <PathControlledDropdown
            name={`${timeConfigPath}.afterWeek.relativeAfterWeekOperator` as Path<T>}
            label=""
            options={WeekReferenceOperatorSchema.options}
          />
        ) : null}
      </Box>

      {/* Third column - Offset number input for relative mode */}
      <Box sx={{ flex: 1 }}>
        {isAfterWeekRelative ? (
          <PathControlledNumberInput
            name={`${timeConfigPath}.afterWeek.relativeAfterWeekOffset` as Path<T>}
            label=""
            min={0}
            max={52}
          />
        ) : null}
      </Box>
    </>
  );

  const renderCustomRangeMode = () => (
    <Flex gap={16} flexDirection="column">
      {renderCustomRangeWeekRow(
        'Start Week',
        isCustomStartRelative,
        `${timeConfigPath}.customRange.relativeStartReference` as Path<T>,
        `${timeConfigPath}.customRange.relativeStartOperator` as Path<T>,
        `${timeConfigPath}.customRange.relativeStartOffset` as Path<T>,
        `${timeConfigPath}.customRange.startWeek` as Path<T>,
        `${timeConfigPath}.customRange.useRelativeStartWeek` as Path<T>
      )}

      {renderCustomRangeWeekRow(
        'End Week',
        isCustomEndRelative,
        `${timeConfigPath}.customRange.relativeEndReference` as Path<T>,
        `${timeConfigPath}.customRange.relativeEndOperator` as Path<T>,
        `${timeConfigPath}.customRange.relativeEndOffset` as Path<T>,
        `${timeConfigPath}.customRange.endWeek` as Path<T>,
        `${timeConfigPath}.customRange.useRelativeEndWeek` as Path<T>
      )}
    </Flex>
  );

  const renderPredefinedPeriodMode = () => (
    <>
      <Box sx={{ flex: 1, maxWidth: DROPDOWN_WIDTH }}>
        <PathControlledDropdown
          name={`${timeConfigPath}.timeRange` as Path<T>}
          label=""
          options={PredefinedPeriodRangesSchema.options}
          placeholder="Select Predefined Period"
        />
      </Box>
      <Box sx={{ flex: 1 }} />
    </>
  );

  const renderPredefinedComparisonMode = () => (
    <>
      <Box sx={{ flex: 1, maxWidth: DROPDOWN_WIDTH }}>
        <PathControlledDropdown
          name={`${timeConfigPath}.timeRange` as Path<T>}
          label=""
          options={PredefinedPeriodRangesSchema.options}
          placeholder="Select Predefined Period"
        />
      </Box>
      <Box sx={{ flex: 1, maxWidth: DROPDOWN_WIDTH }}>
        <PathControlledDropdown
          name={`${timeConfigPath}.comparisonOption` as Path<T>}
          label=""
          options={PredefinedComparisonOptionsSchema.options}
          placeholder="Select Comparison Option"
        />
      </Box>
    </>
  );

  const renderForTheFirstTimeMode = () => (
    <>
      <Box sx={{ flex: 1, maxWidth: DROPDOWN_WIDTH }}>
        <PathControlledDropdown
          name={`${timeConfigPath}.forTheFirstTimeOption` as Path<T>}
          label=""
          options={ForTheFirstTimeOptionsSchema.options}
          placeholder="Select For the First Time Option"
        />
      </Box>
    </>
  );

  return (
    <Flex flexDirection="column" gap={0} style={{ marginBottom: '16px' }}>
      {/* First row: Time Range Type dropdown with flex width and positioned Relative checkbox */}
      <Flex alignItems="flex-start" gap={16} style={{ position: 'relative', marginBottom: '16px' }}>
        {/* Time Range Type - flex width to match upper dropdowns */}
        <Box sx={{ flex: 1, maxWidth: DROPDOWN_WIDTH }}>
          <PathControlledDropdown
            name={`${timeConfigPath}.mode` as Path<T>}
            label="Time Range Type"
            options={TimeRangeModesSchema.options}
            placeholder="Select Time Range Type"
          />
        </Box>

        {/* Empty flex item to match the upper row structure */}
        <Box sx={{ flex: 1, position: 'relative' }}>
          {/* Relative checkbox for Before Week - positioned independently */}
          {timeConfig.mode === 'Before Week' && (
            <RelativeModeCheckbox name={`${timeConfigPath}.beforeWeek.useRelativeBeforeWeek` as Path<T>} />
          )}

          {/* Relative checkbox for After Week - positioned independently */}
          {timeConfig.mode === 'After Week' && (
            <RelativeModeCheckbox name={`${timeConfigPath}.afterWeek.useRelativeAfterWeek` as Path<T>} />
          )}
        </Box>
      </Flex>

      {/* Second row: Compact content based on selected type */}
      <Flex gap={16} alignItems="center">
        {timeConfig.mode === 'Custom Range' && renderCustomRangeMode()}
        {timeConfig.mode === 'Before Week' && renderBeforeWeekMode()}
        {timeConfig.mode === 'After Week' && renderAfterWeekMode()}
        {timeConfig.mode === 'Predefined Period' && renderPredefinedPeriodMode()}
        {timeConfig.mode === 'Predefined Comparison' && renderPredefinedComparisonMode()}
        {timeConfig.mode === 'For the First Time' && renderForTheFirstTimeMode()}
      </Flex>
    </Flex>
  );
}

export default RuleBlockTimeRange;
